import React, { useEffect, useState } from "react";
import axios from "axios";
import { saveAuth } from "./auth";
import "./LoginPage.css";
import { clearShopConfigCache, getShopConfig } from "./shopConfig";
/* import deskposIcon from "./assets/deskpos-icon.png"; */
import deskposLogo from "./assets/deskpos-logo-desktop.png";

const CLOUD_API_URL =
  process.env.REACT_APP_API_URL ||
  "https://api.deskpos.ie";

const LOCAL_API_URL =
  process.env.REACT_APP_LOCAL_API_URL ||
  "http://localhost:7000";

const LOCAL_LOGIN_TIMEOUT = 1500;
const CLOUD_LOGIN_TIMEOUT = 4000;
const ACTIVATE_TIMEOUT = 2500;

function LoginPage({ mode = "pos" }) {
  const isAdminLogin = mode === "admin";

  /*
    We keep the existing state name "email"
    to avoid unnecessary changes elsewhere.

    For POS login this field can now contain:
    - Name / username
    - Email

    For Admin login it remains email only.
  */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [appVersion, setAppVersion] = useState("");
  const [updateMessage, setUpdateMessage] = useState("");
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [updateDownloaded, setUpdateDownloaded] = useState(false);

  const [verificationRequired, setVerificationRequired] =
    useState(false);

  const [verificationId, setVerificationId] =
    useState(null);

  const [verificationCode, setVerificationCode] =
    useState("");

  const [maskedEmail, setMaskedEmail] =
    useState("");

  const [verifyingCode, setVerifyingCode] =
    useState(false);

  const [trustDevice, setTrustDevice] =
    useState(false);

  const [allowTrustedDevice, setAllowTrustedDevice] =
    useState(false);

  useEffect(() => {
    const ipcRenderer =
      window.electron?.ipcRenderer;

    if (!ipcRenderer) {
      return;
    }

    const loadVersion = async () => {
      try {
        const version =
          await ipcRenderer.invoke(
            "get-app-version"
          );

        setAppVersion(version || "");
      } catch (error) {
        console.error(
          "Could not read app version:",
          error
        );
      }
    };

    loadVersion();

    const removeUpdateListener =
      ipcRenderer.on(
        "update-status",
        (data) => {
          if (!data) return;

          if (data.status === "available") {
            setUpdateMessage(
              data.message ||
                `DeskPOS ${data.version} is available.`
            );
          }

          if (data.status === "downloading") {
            setUpdateMessage(
              data.message ||
                `Downloading update: ${Math.round(
                  data.percent || 0
                )}%`
            );
          }

          if (data.status === "downloaded") {
            setUpdateMessage(
              data.message ||
                "Update downloaded and ready to install."
            );

            setUpdateDownloaded(true);
            setCheckingUpdate(false);
          }

          if (
            data.status ===
            "not-available"
          ) {
            setUpdateMessage(
              data.message ||
                "DeskPOS is up to date."
            );

            setCheckingUpdate(false);
          }

          if (data.status === "error") {
            setUpdateMessage(
              data.message ||
                "Could not check for updates."
            );

            setCheckingUpdate(false);
          }
        }
      );

    return () => {
      if (
        typeof removeUpdateListener ===
        "function"
      ) {
        removeUpdateListener();
      }
    };
  }, []);

  const handleCheckForUpdates = async () => {
    const ipcRenderer =
      window.electron?.ipcRenderer;

    if (!ipcRenderer) {
      setUpdateMessage(
        "Update checking is only available in the installed DeskPOS app."
      );

      return;
    }

    try {
      setCheckingUpdate(true);
      setUpdateDownloaded(false);
      setUpdateMessage(
        "Checking for updates..."
      );

      const result =
        await ipcRenderer.invoke(
          "check-for-updates"
        );

      if (!result?.success) {
        setUpdateMessage(
          result?.message ||
            "Could not check for updates."
        );

        setCheckingUpdate(false);
      }
    } catch (error) {
      console.error(
        "Update check failed:",
        error
      );

      setUpdateMessage(
        "Could not check for updates."
      );

      setCheckingUpdate(false);
    }
  };

  const handleInstallUpdate = async () => {
    const ipcRenderer =
      window.electron?.ipcRenderer;

    if (!ipcRenderer) return;

    try {
      setUpdateMessage(
        "Restarting DeskPOS to install the update..."
      );

      const result =
        await ipcRenderer.invoke(
          "install-downloaded-update"
        );

      if (!result?.success) {
        setUpdateMessage(
          result?.message ||
            "No downloaded update is ready."
        );
      }
    } catch (error) {
      console.error(
        "Update installation failed:",
        error
      );

      setUpdateMessage(
        "Could not install the update."
      );
    }
  };

  const getErrorMessage = (err) => {
    return (
      err.response?.data?.error ||
      err.response?.data?.details ||
      err.message ||
      "Login failed"
    );
  };

  const redirectAfterLogin = (user) => {
    const APP_MODE =
      process.env.REACT_APP_APP_MODE ||
      "admin";

    if (
      !isAdminLogin &&
      APP_MODE === "till"
    ) {
      window.location.hash = "/";
      return;
    }

    if (user.role === "super_admin") {
      window.location.hash =
        "/super-admin";
    } else if (
      user.role === "shop_admin" ||
      user.role === "manager"
    ) {
      window.location.hash =
        "/dashboard";
    } else {
      window.location.hash = "/";
    }
  };

  const activateLocalTill = async (
    user
  ) => {
    if (
      !user ||
      user.role === "super_admin"
    ) {
      return;
    }

    let licenseKey =
      user.license_key ||
      user.licenseKey ||
      "";

    if (!licenseKey) {
      try {
        const config =
          await getShopConfig();

        licenseKey =
          config.license_key ||
          config.licenseKey ||
          "";
      } catch (err) {
        console.log(
          "Could not read licence key from local config:",
          err.message
        );
      }
    }

    if (!licenseKey) {
      console.log(
        "No licence key found, local activation skipped"
      );

      return;
    }

    try {
      let drawerComPort = "";

      try {
        const localConfigRes =
          await axios.get(
            `${LOCAL_API_URL}/local-config`,
            {
              timeout: 1500,
            }
          );

        drawerComPort =
          localConfigRes.data
            ?.drawer_com_port ||
          localConfigRes.data
            ?.drawerComPort ||
          "";
      } catch (err) {
        console.log(
          "Could not read local drawer port before activation:",
          err.message
        );
      }

      await axios.post(
        `${LOCAL_API_URL}/activate`,
        {
          license_key: licenseKey,
          drawer_com_port:
            drawerComPort,
        },
        {
          timeout:
            ACTIVATE_TIMEOUT,
        }
      );

      clearShopConfigCache();

      console.log(
        "Local till activated/refreshed"
      );
    } catch (activateErr) {
      console.log(
        "Local activate skipped/failed:",
        activateErr.response?.data ||
          activateErr.message
      );
    }
  };

  const TRUSTED_DEVICE_STORAGE_KEY =
    "deskpos_trusted_device_token";

  const getTrustedDeviceToken = () => {
    return (
      localStorage.getItem(
        TRUSTED_DEVICE_STORAGE_KEY
      ) || ""
    );
  };

  const saveTrustedDeviceToken = (
    token
  ) => {
    if (token) {
      localStorage.setItem(
        TRUSTED_DEVICE_STORAGE_KEY,
        token
      );
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError("");

    /*
      POS:
        Shaun
        OR
        shaun@email.com

      Admin:
        email only
    */
    const cleanLogin = String(
      email || ""
    )
      .trim()
      .toLowerCase();

    let res = null;

    let localLoginError = null;
    let cloudLoginError = null;

    try {
      if (
        !cleanLogin ||
        !password
      ) {
        throw new Error(
          isAdminLogin
            ? "Email and password required"
            : "Username/email and password required"
        );
      }

      // =========================
      // ADMIN LOGIN
      // Cloud only, email only
      // =========================

      if (isAdminLogin) {
        try {
          res = await axios.post(
            `${CLOUD_API_URL}/auth/login`,
            {
              email: cleanLogin,
              password,
            },
            {
              timeout:
                CLOUD_LOGIN_TIMEOUT,

              headers: {
                "x-deskpos-device-token":
                  getTrustedDeviceToken(),
              },
            }
          );

          console.log(
            "Admin logged in using cloud server"
          );
        } catch (cloudErr) {
          cloudLoginError =
            cloudErr;

          console.log(
            "Cloud admin login failed:",
            cloudErr.response?.data ||
              cloudErr.message
          );
        }
      } else {
        // =========================
        // POS LOGIN
        // Local/offline first
        // Username OR email
        // =========================

        try {
          res = await axios.post(
            `${LOCAL_API_URL}/auth/login`,
            {
              login: cleanLogin,
              password,
            },
            {
              timeout:
                LOCAL_LOGIN_TIMEOUT,
            }
          );

          console.log(
            "POS logged in using local server"
          );
        } catch (localErr) {
          localLoginError =
            localErr;

          console.log(
            "Local POS login failed:",
            localErr.response?.data ||
              localErr.message
          );
        }

        // =========================
        // POS CLOUD FALLBACK
        //
        // Cloud login still expects
        // EMAIL ONLY.
        //
        // So only attempt cloud login
        // if user typed an email.
        // =========================

        if (
          !res &&
          cleanLogin.includes("@")
        ) {
          try {
            res = await axios.post(
              `${CLOUD_API_URL}/auth/login`,
              {
                email: cleanLogin,
                password,
              },
              {
                timeout:
                  CLOUD_LOGIN_TIMEOUT,

                headers: {
                  "x-deskpos-device-token":
                    getTrustedDeviceToken(),
                },
              }
            );

            console.log(
              "POS logged in using cloud server"
            );
          } catch (cloudErr) {
            cloudLoginError =
              cloudErr;

            console.log(
              "Cloud POS login failed:",
              cloudErr.response
                ?.data ||
                cloudErr.message
            );
          }
        }
      }

      if (!res) {
        if (isAdminLogin) {
          const cloudMessage =
            cloudLoginError
              ? getErrorMessage(
                  cloudLoginError
                )
              : "Cloud login did not respond";

          throw new Error(
            `Admin login failed: ${cloudMessage}`
          );
        }

        const localMessage =
          localLoginError
            ? getErrorMessage(
                localLoginError
              )
            : "Local login did not respond";

        const cloudMessage =
          cleanLogin.includes("@")
            ? cloudLoginError
              ? getErrorMessage(
                  cloudLoginError
                )
              : "Cloud login did not respond"
            : "Cloud fallback requires an email address";

        throw new Error(
          `Offline login failed: ${localMessage}. ${cloudMessage}`
        );
      }

      // =========================
      // EMAIL VERIFICATION
      // Admin/cloud flow only
      // =========================

      if (
        res.data
          .verification_required
      ) {
        setVerificationRequired(
          true
        );

        setVerificationId(
          res.data.verification_id
        );

        setMaskedEmail(
          res.data.email_hint ||
            cleanLogin
        );

        setAllowTrustedDevice(
          res.data
            .allow_trusted_device ===
            true
        );

        setTrustDevice(false);

        setVerificationCode("");

        return;
      }

      const user =
        res.data.user;

      if (!user) {
        throw new Error(
          "Login response missing user data"
        );
      }

      console.log(
        "LOGIN USER:",
        user
      );

      // =========================
      // POS SHOP SAFETY CHECK
      // =========================

      if (
        !isAdminLogin &&
        user.role !== "super_admin"
      ) {
        try {
          const localConfigRes =
            await axios.get(
              `${LOCAL_API_URL}/local-config`,
              {
                timeout: 1500,
              }
            );

          const localConfig =
            localConfigRes.data || {};

          const localShopId =
            Number(
              localConfig.shop_id ||
                0
            );

          const loginShopId =
            Number(
              user.shop_id ||
                user.shopId ||
                0
            );

          const hasLocalActivation =
            !!localConfig.license_key ||
            !!localConfig.licenseKey ||
            !!localConfig.activated_at ||
            !!localConfig.activatedAt;

          console.log(
            "POS local config:",
            localConfig
          );

          console.log(
            "POS local shop id:",
            localShopId
          );

          console.log(
            "Login user shop id:",
            loginShopId
          );

          console.log(
            "Has local activation:",
            hasLocalActivation
          );

          if (
            hasLocalActivation &&
            localShopId &&
            loginShopId &&
            localShopId !==
              loginShopId
          ) {
            setError(
              `This till is activated for shop ${localShopId}. You are trying to login with shop ${loginShopId} user. Please use the correct shop user or re-activate this till.`
            );

            return;
          }
        } catch (configErr) {
          console.log(
            "Could not check local till shop:",
            configErr.message
          );
        }
      }

      saveAuth(res.data);

      redirectAfterLogin(user);

      // Only POS login should activate local till.
      if (!isAdminLogin) {
        activateLocalTill(
          user
        ).catch((err) => {
          console.log(
            "Background local activation skipped/failed:",
            err.message
          );
        });
      }
    } catch (err) {
      console.error(
        "Login error:",
        err.response?.data ||
          err.message
      );

      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.message ||
          "Login failed"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (
    e
  ) => {
    e.preventDefault();

    setVerifyingCode(true);
    setError("");

    try {
      const cleanCode = String(
        verificationCode || ""
      ).trim();

      if (
        !/^\d{6}$/.test(
          cleanCode
        )
      ) {
        throw new Error(
          "Please enter the 6-digit verification code."
        );
      }

      if (!verificationId) {
        throw new Error(
          "Verification request is missing. Please login again."
        );
      }

      const res =
        await axios.post(
          `${CLOUD_API_URL}/auth/verify-email-code`,
          {
            verification_id:
              verificationId,

            code: cleanCode,

            device_name:
              "DeskPOS Admin Device",

            trust_device:
              allowTrustedDevice &&
              trustDevice,
          },
          {
            timeout:
              CLOUD_LOGIN_TIMEOUT,
          }
        );

      const user =
        res.data.user;

      if (
        !user ||
        !res.data.token
      ) {
        throw new Error(
          "Verification response is incomplete."
        );
      }

      const trustedDeviceToken =
        res.data.trusted_device
          ?.token;

      if (
        trustedDeviceToken
      ) {
        saveTrustedDeviceToken(
          trustedDeviceToken
        );
      }

      saveAuth(res.data);

      redirectAfterLogin(user);
    } catch (err) {
      console.error(
        "Verification error:",
        err.response?.data ||
          err.message
      );

      setError(
        err.response?.data?.error ||
          err.message ||
          "Verification failed"
      );
    } finally {
      setVerifyingCode(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="deskpos-login-logo-wrap">
          <img
            src={deskposLogo}
            alt="DeskPOS"
            className="deskpos-login-logo-image"
          />
        </div>

        <h1>
          {isAdminLogin
            ? "Admin Login"
            : "POS Login"}
        </h1>

        <p>
          {isAdminLogin
            ? "Sign in to your admin dashboard."
            : "Sign in to your till account."}
        </p>

        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        {!verificationRequired ? (
          <form
            onSubmit={handleLogin}
          >
            <label>
              {isAdminLogin
                ? "Email"
                : "Username or Email"}

              <input
                type={
                  isAdminLogin
                    ? "email"
                    : "text"
                }
                value={email}
                onChange={(e) =>
                  setEmail(
                    e.target.value
                  )
                }
                placeholder={
                  isAdminLogin
                    ? "Enter your email"
                    : "Enter name or email"
                }
                autoComplete={
                  isAdminLogin
                    ? "email"
                    : "username"
                }
                autoFocus
              />
            </label>

            <label>
              Password

              <input
                type="password"
                value={password}
                onChange={(e) =>
                  setPassword(
                    e.target.value
                  )
                }
                autoComplete="current-password"
              />
            </label>

            <button
              type="submit"
              disabled={loading}
            >
              {loading
                ? "Logging in..."
                : "Login"}
            </button>

            {!isAdminLogin && (
              <div className="activation-buttons">
                <button
                  type="button"
                  className="activate-link-btn"
                  onClick={() => {
                    window.location.hash =
                      "/activate-till";
                  }}
                >
                  Activate / Switch
                  Till
                </button>

                <button
                  type="button"
                  className="activate-link-btn"
                  onClick={() => {
                    window.location.hash =
                      "/activate";
                  }}
                >
                  Activate Licence
                </button>
              </div>
            )}

            {window.electron
              ?.ipcRenderer && (
              <div className="login-update-box">
                <div className="login-update-header">
                  <span>
                    DeskPOS Version
                  </span>

                  <strong>
                    {appVersion ||
                      "Checking..."}
                  </strong>
                </div>

                {updateMessage && (
                  <div className="login-update-message">
                    {
                      updateMessage
                    }
                  </div>
                )}

                <div className="login-update-buttons">
                  <button
                    type="button"
                    className="login-update-btn"
                    onClick={
                      handleCheckForUpdates
                    }
                    disabled={
                      checkingUpdate
                    }
                  >
                    {checkingUpdate
                      ? "Checking..."
                      : "Check for Updates"}
                  </button>

                  {updateDownloaded && (
                    <button
                      type="button"
                      className="login-install-update-btn"
                      onClick={
                        handleInstallUpdate
                      }
                    >
                      Restart &
                      Update
                    </button>
                  )}
                </div>
              </div>
            )}
          </form>
        ) : (
          <form
            onSubmit={
              handleVerifyCode
            }
          >
            <p>
              We sent a 6-digit
              verification code to{" "}
              <strong>
                {maskedEmail}
              </strong>
              .
            </p>

            <label>
              Verification code

              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={
                  verificationCode
                }
                onChange={(e) => {
                  const numbersOnly =
                    e.target.value.replace(
                      /\D/g,
                      ""
                    );

                  setVerificationCode(
                    numbersOnly
                  );
                }}
                autoFocus
                autoComplete="one-time-code"
              />
            </label>

            {allowTrustedDevice && (
              <div className="trust-device-card">
                <label className="trust-device-label">
                  <input
                    type="checkbox"
                    checked={
                      trustDevice
                    }
                    onChange={(e) =>
                      setTrustDevice(
                        e.target
                          .checked
                      )
                    }
                  />

                  <div className="trust-device-text">
                    <div className="trust-device-title">
                      Trust this
                      device for 30
                      days
                    </div>

                    <div className="trust-device-desc">
                      You won't need
                      another
                      verification
                      code on this
                      device for the
                      next 30 days.
                    </div>
                  </div>
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={
                verifyingCode
              }
            >
              {verifyingCode
                ? "Verifying..."
                : "Verify & Login"}
            </button>

            <button
              type="button"
              className="activate-link-btn"
              onClick={() => {
                setVerificationRequired(
                  false
                );

                setVerificationId(
                  null
                );

                setVerificationCode(
                  ""
                );

                setMaskedEmail("");

                setAllowTrustedDevice(
                  false
                );

                setTrustDevice(false);

                setError("");
              }}
            >
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default LoginPage;